import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {CatalogOtherField} from '@/components/catalog-other-field'
import {FieldError} from '@/components/field-error'
import {FieldGroup, NumberField, SelectField} from '@/components/wizard-fields'
import type {Draft, PerfilSociodemograficoUpdate} from '@/hooks/empresa/mi_espacio/use-perfil-sociodemografico'
import {tope} from '@/lib/campos-texto'
import {validatePhone} from '@/lib/validation'

const ESTADO_CIVIL = ['soltero', 'casado', 'union_libre', 'separado', 'divorciado', 'viudo'] as const
const TIPO_VIVIENDA = ['propia', 'arrendada', 'familiar'] as const
const TIPO_SANGRE = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'] as const
const PARENTESCO_OPTIONS = ['Padre', 'Madre', 'Hijo', 'Hija', 'Esposo', 'Esposa', 'Hermano', 'Hermana'] as const

export function PersonalStepFields({
  draft,
  godReadOnly,
  updateField,
  commit,
}: {
  draft: Draft
  godReadOnly: boolean
  updateField: (field: keyof Draft, value: string) => void
  commit: (fields: Partial<PerfilSociodemograficoUpdate>) => void
}) {
  const {t} = useTranslation()
  const [contactoTelefonoError, setContactoTelefonoError] = useState<string | undefined>(undefined)

  function blurContactoTelefono(raw: string) {
    const error = raw.trim() ? validatePhone(raw) : null
    setContactoTelefonoError(error ?? undefined)
    if (!error) commit({contacto_emergencia_telefono: raw.trim() || null})
  }

  return (
    <FieldGroup>
      <SelectField
        id="perfil-sd-estado-civil"
        label={t('perfilSociodemografico.estadoCivil')}
        value={draft.estado_civil}
        options={ESTADO_CIVIL}
        optionLabel={(o) => t(`perfilSociodemografico.estadoCivil.${o}`)}
        unspecifiedLabel={t('perfilSociodemografico.unspecified')}
        disabled={godReadOnly}
        onChange={(v) => {
          updateField('estado_civil', v)
          commit({estado_civil: (v || null) as PerfilSociodemograficoUpdate['estado_civil']})
        }}
      />
      <NumberField
        id="perfil-sd-numero-hijos"
        label={t('perfilSociodemografico.numeroHijos')}
        value={draft.numero_hijos}
        min={0}
        max={30}
        disabled={godReadOnly}
        onChange={(v) => updateField('numero_hijos', v)}
        onCommit={(v) => commit({numero_hijos: v})}
      />
      <NumberField
        id="perfil-sd-personas-a-cargo"
        label={t('perfilSociodemografico.personasACargo')}
        value={draft.personas_a_cargo}
        min={0}
        max={30}
        disabled={godReadOnly}
        onChange={(v) => updateField('personas_a_cargo', v)}
        onCommit={(v) => commit({personas_a_cargo: v})}
      />
      <NumberField
        id="perfil-sd-integrantes-hogar"
        label={t('perfilSociodemografico.integrantesHogar')}
        value={draft.integrantes_hogar}
        min={0}
        max={30}
        disabled={godReadOnly}
        onChange={(v) => updateField('integrantes_hogar', v)}
        onCommit={(v) => commit({integrantes_hogar: v})}
      />
      <SelectField
        id="perfil-sd-tipo-vivienda"
        label={t('perfilSociodemografico.tipoVivienda')}
        value={draft.tipo_vivienda}
        options={TIPO_VIVIENDA}
        optionLabel={(o) => t(`perfilSociodemografico.tipoVivienda.${o}`)}
        unspecifiedLabel={t('perfilSociodemografico.unspecified')}
        disabled={godReadOnly}
        onChange={(v) => {
          updateField('tipo_vivienda', v)
          commit({tipo_vivienda: (v || null) as PerfilSociodemograficoUpdate['tipo_vivienda']})
        }}
      />
      <SelectField
        id="perfil-sd-tipo-sangre"
        label={t('perfilSociodemografico.tipoSangre')}
        value={draft.tipo_sangre}
        options={TIPO_SANGRE}
        optionLabel={(o) => t(`perfilSociodemografico.tipoSangre.${o}`)}
        unspecifiedLabel={t('perfilSociodemografico.unspecified')}
        disabled={godReadOnly}
        onChange={(v) => {
          updateField('tipo_sangre', v)
          commit({tipo_sangre: (v || null) as PerfilSociodemograficoUpdate['tipo_sangre']})
        }}
      />
      <div className="space-y-2 border-t border-border pt-4">
        <p className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
          {t('perfilSociodemografico.contactoEmergencia')}
        </p>
        <Label htmlFor="perfil-sd-contacto-nombre">{t('perfilSociodemografico.contactoNombre')}</Label>
        <Input
          id="perfil-sd-contacto-nombre"
          value={draft.contacto_emergencia_nombre}
          maxLength={120}
          disabled={godReadOnly}
          onChange={(e) => updateField('contacto_emergencia_nombre', e.target.value)}
          onBlur={(e) => commit({contacto_emergencia_nombre: e.target.value || null})}
        />
      </div>
      <CatalogOtherField
        id="perfil-sd-contacto-parentesco"
        label={t('perfilSociodemografico.contactoParentesco')}
        options={PARENTESCO_OPTIONS}
        value={draft.contacto_emergencia_parentesco}
        disabled={godReadOnly}
        onChange={(v) => updateField('contacto_emergencia_parentesco', v)}
        onCommit={(v) => commit({contacto_emergencia_parentesco: v || null})}
      />
      <div className="space-y-2">
        <Label htmlFor="perfil-sd-contacto-telefono">{t('perfilSociodemografico.contactoTelefono')}</Label>
        <Input
          id="perfil-sd-contacto-telefono"
          inputMode="tel"
          value={draft.contacto_emergencia_telefono}
          maxLength={tope('TEL')}
          disabled={godReadOnly}
          aria-invalid={!!contactoTelefonoError}
          onChange={(e) => {
            updateField('contacto_emergencia_telefono', e.target.value)
            setContactoTelefonoError(undefined)
          }}
          onBlur={(e) => blurContactoTelefono(e.target.value)}
        />
        <FieldError error={contactoTelefonoError} />
      </div>
    </FieldGroup>
  )
}
